import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import React, { useCallback, useMemo, useState } from 'react';
import { FaChevronLeft, FaDownload } from 'react-icons/fa';
import { generateCubeSql } from './cube/cubeService';
import { DownloadSummary } from './DownloadSummary';
import { SequenceAvailabilityNotice } from './SequenceAvailabilityNotice';
import {
  formatFileSize,
  getEstimatedSizeInBytes,
  getEstimatedUnzippedSizeInBytes,
  getSequenceAvailability,
  requiresSequences,
  supportsExtensions,
} from './utils';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/reactRouterPlugins';
import { Message } from '@/components/message';
import { ALLOWED_URI_REGEXP } from '@/components/hyperText';
import { useChecklistKey } from '@/hooks/useChecklistKey';

interface TermsStepProps {
  predicate?: any;
  sql?: string;
  selectedFormat: any;
  configuration: any;
  totalRecords?: number;
  sequencedRecords?: number;
  loadingCounts?: boolean;
  onBack: () => void;
  source?: string | null;
}

type AcceptedTerm = 'dataUse' | 'largeDownload' | 'dataCitation';

// Constants from portal16
const LARGE_DOWNLOAD_OFFSET = 1048576; // 1M records - above this size, not possible to handle in Excel

interface DownloadTimeEstimate {
  speed: number;
  hours: number;
  minutes: number;
  formattedTime: string;
}

// Map the download API's response status to a distinct translated error message.
// The API returns 400 for invalid requests, 403 for missing/wrong authentication,
// 420 for too many concurrent downloads, and 500/503 for server errors.
function messageIdForDownloadError(status: number): string {
  if (status === 400) return 'occurrenceDownloadFlow.invalidDownloadRequest';
  if (status === 401 || status === 403) return 'occurrenceDownloadFlow.notAuthorizedForDownload';
  if (status === 420) return 'occurrenceDownloadFlow.slowDown';
  if (status >= 500) return 'occurrenceDownloadFlow.downloadServiceUnavailable';
  return 'occurrenceDownloadFlow.unableToCreateDownload';
}

function calculateDownloadTime(sizeInBytes: number, speedMbps: number): DownloadTimeEstimate {
  const sizeInMegabits = (sizeInBytes * 8) / (1024 * 1024);
  const timeInSeconds = sizeInMegabits / speedMbps;
  const hours = Math.floor(timeInSeconds / 3600);
  const minutes = Math.round((timeInSeconds % 3600) / 60);

  let formattedTime = '';
  if (hours > 0) {
    formattedTime = `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    if (minutes > 0) {
      formattedTime += ` ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
    }
  } else {
    formattedTime = `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  }

  return {
    speed: speedMbps,
    hours,
    minutes,
    formattedTime,
  };
}

export default function TermsStep({
  predicate,
  selectedFormat,
  configuration,
  totalRecords,
  sequencedRecords,
  loadingCounts,
  onBack,
  source,
}: TermsStepProps) {
  const currentContextChecklistKey = useChecklistKey();
  const intl = useIntl();
  const navigate = useNavigate();
  const { localizeLink } = useI18n();
  const isSequenceFormat = requiresSequences(selectedFormat.id);
  // A sequence-only download holds the sequenced subset of the search, so that - not the search
  // total - is what the size estimate and the large download warning are about.
  const recordsInDownload = (isSequenceFormat ? sequencedRecords : totalRecords) ?? 0;
  // Get zipped download size (what user downloads)
  const estimatedSizeInBytes = getEstimatedSizeInBytes(selectedFormat.id, recordsInDownload);
  // Get unzipped size (disk space needed after extraction)
  const estimatedUnzippedSizeInBytes = getEstimatedUnzippedSizeInBytes(
    selectedFormat.id,
    recordsInDownload
  );
  const isLargeDownload = ['SIMPLE_CSV', 'DWCA'].includes(selectedFormat.id)
    ? recordsInDownload > LARGE_DOWNLOAD_OFFSET
    : false;
  // The filters stay editable while the flow is open, so the format can stop being usable after it
  // was picked.
  const blockedByMissingSequences =
    isSequenceFormat &&
    getSequenceAvailability({ totalRecords, sequencedRecords, loading: loadingCounts }) === 'none';

  const [preparingDownload, setPreparingDownload] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState({
    dataUse: false,
    dataCitation: false,
    ...(isLargeDownload && { largeDownload: false }),
  });

  const allTermsAccepted = Object.values(acceptedTerms).every(Boolean);

  // Get the primary download time estimate (50 Mbps)
  const primaryEstimate = useMemo(() => {
    return calculateDownloadTime(estimatedSizeInBytes, 50);
  }, [estimatedSizeInBytes]);

  const handleDownload = useCallback(async () => {
    setPreparingDownload(true);
    let sql = configuration.sql;
    let machineDescription = configuration.machineDescription;
    if (configuration.cube) {
      const result = await generateCubeSql(
        configuration.cube,
        predicate,
        currentContextChecklistKey
      );
      if (result.error) {
        alert(intl.formatMessage({ id: 'customSqlDownload.errorGeneratingSql' }));
        setPreparingDownload(false);
        return;
      }
      sql = result.sql;
      machineDescription = result.machineDescription;
    }

    const format = selectedFormat.downloadFormat ?? selectedFormat.id;
    fetch('/api/user/download/predicate?source=' + encodeURIComponent(source ?? ''), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        predicate,
        format: format,
        // The taxonomy of a free text SQL download is defined by the SQL itself
        checklistKey: selectedFormat.id === 'SQL_TSV_ZIP' ? undefined : configuration.checklistKey,
        verbatimExtensions: supportsExtensions(selectedFormat.id) ? configuration.extensions : [],
        machineDescription: selectedFormat.id === 'SQL_CUBE' ? machineDescription : undefined,
        sql: format === 'SQL_TSV_ZIP' ? sql : undefined,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          setError(intl.formatMessage({ id: messageIdForDownloadError(response.status) }));
          return;
        }
        return response.json();
      })
      .then((data) => {
        if (data) {
          navigate(localizeLink(`/occurrence/download/${data.downloadKey}`));
        }
      })
      .catch((error) => {
        console.error('There was a problem with the download request:', error);
        setError(intl.formatMessage({ id: 'occurrenceDownloadFlow.unableToCreateDownload' }));
      })
      .finally(() => {
        setPreparingDownload(false);
      });
  }, [configuration, predicate, selectedFormat, intl, navigate, localizeLink, source]);

  const handleTermChange = (term: AcceptedTerm) => {
    setAcceptedTerms((prev) => ({
      ...prev,
      [term]: !prev[term],
    }));
  };

  return (
    <div className="g-max-w-4xl g-mx-auto">
      {/* Header */}
      <div className="g-mb-4">
        <button
          onClick={onBack}
          className="g-flex g-items-center g-gap-2 g-text-gray-600 hover:g-text-gray-900 g-mb-4 g-transition-colors"
        >
          <FaChevronLeft size={20} />
          <FormattedMessage id="occurrenceDownloadFlow.backToConfiguration" />
        </button>
      </div>

      <div className="g-grid lg:g-grid-cols-3 g-gap-8">
        {/* Terms Content */}
        <div className="lg:g-col-span-2 g-space-y-6">
          {/* Sequence availability - the filters can change while this step is open */}
          {isSequenceFormat && (
            <SequenceAvailabilityNotice
              totalRecords={totalRecords}
              sequencedRecords={sequencedRecords}
              loading={loadingCounts}
            />
          )}

          {/* Data Use Agreement */}
          <div className="g-bg-white g-rounded g-shadow-md g-border g-border-gray-200">
            <div className="g-p-6">
              <div className="g-flex g-items-start g-gap-4">
                <div className="g-flex-shrink-0 g-mt-1">
                  <Checkbox
                    id="dataUse"
                    checked={acceptedTerms.dataUse}
                    onCheckedChange={() => handleTermChange('dataUse')}
                    className="g-h-5 g-w-5"
                  />
                </div>
                <div className="g-flex-1">
                  <label
                    htmlFor="dataUse"
                    className="g-flex g-items-center g-gap-2 g-font-semibold g-text-gray-900 g-mb-2 g-cursor-pointer"
                  >
                    <FormattedMessage id="occurrenceDownloadFlow.dataUseAgreementTitle" />
                  </label>
                  <div className="g-text-gray-700 g-space-y-2 g-prose">
                    <Message
                      id="occurrenceDownloadFlow.dataUseAgreementText"
                      sanitizeOptions={{
                        ALLOWED_URI_REGEXP,
                        ALLOWED_TAGS: [
                          'a',
                          'strong',
                          'em',
                          'p',
                          'br',
                          'li',
                          'ul',
                          'ol',
                          'code',
                          'pre',
                        ],
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Citation */}
          <div className="g-bg-white g-rounded g-shadow-md g-border g-border-gray-200">
            <div className="g-p-6">
              <div className="g-flex g-items-start g-gap-4">
                <div className="g-flex-shrink-0 g-mt-1">
                  <Checkbox
                    id="dataCitation"
                    checked={acceptedTerms.dataCitation}
                    onCheckedChange={() => handleTermChange('dataCitation')}
                    className="g-h-5 g-w-5"
                  />
                </div>
                <div className="g-flex-1">
                  <label
                    htmlFor="dataCitation"
                    className="g-flex g-items-center g-gap-2 g-font-semibold g-text-gray-900 g-mb-2 g-cursor-pointer"
                  >
                    <FormattedMessage id="occurrenceDownloadFlow.dataCitationTitle" />
                  </label>
                  <div className="g-text-gray-700 g-space-y-2 g-prose">
                    <div>
                      <Message
                        id="occurrenceDownloadFlow.dataCitationText"
                        sanitizeOptions={{
                          ALLOWED_URI_REGEXP,
                          ALLOWED_TAGS: [
                            'a',
                            'strong',
                            'em',
                            'p',
                            'br',
                            'li',
                            'ul',
                            'ol',
                            'code',
                            'pre',
                          ],
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Large Download Warning - Conditional */}
          {isLargeDownload && (
            <div className="g-bg-white g-rounded g-shadow-md g-border g-border-gray-200">
              <div className="g-p-6">
                <div className="g-flex g-items-start g-gap-4">
                  <div className="g-flex-shrink-0 g-mt-1">
                    <Checkbox
                      id="largeDownload"
                      checked={acceptedTerms.largeDownload || false}
                      onCheckedChange={() => handleTermChange('largeDownload')}
                      className="g-h-5 g-w-5"
                    />
                  </div>
                  <div className="g-flex-1">
                    <label
                      htmlFor="largeDownload"
                      className="g-flex g-items-center g-gap-2 g-font-semibold g-text-gray-900 g-mb-2 g-cursor-pointer"
                    >
                      <FormattedMessage id="occurrenceDownloadFlow.largeDownloadAcknowledgment" />
                    </label>
                    <div className="g-text-gray-700 g-space-y-3 g-prose">
                      <div>
                        <p>
                          <FormattedMessage
                            id="occurrenceDownloadFlow.largeDownloadEstimatedSize"
                            values={{
                              size: <strong>{formatFileSize(estimatedSizeInBytes)}</strong>,
                            }}
                          />{' '}
                          <FormattedMessage
                            id="occurrenceDownloadFlow.largeDownloadConnectionSpeed"
                            values={{
                              speed: primaryEstimate.speed,
                              time: <strong>{primaryEstimate.formattedTime}</strong>,
                            }}
                          />
                        </p>
                        <p>
                          <FormattedMessage
                            id="occurrenceDownloadFlow.largeDownloadDiskSpace"
                            values={{
                              size: <strong>{formatFileSize(estimatedUnzippedSizeInBytes)}</strong>,
                            }}
                          />
                        </p>
                        <p>
                          <FormattedMessage
                            id="occurrenceDownloadFlow.largeDownloadRowCount"
                            values={{
                              count: (
                                <strong>
                                  <FormattedNumber value={recordsInDownload} />
                                </strong>
                              ),
                            }}
                          />
                        </p>
                        <p>
                          <FormattedMessage id="occurrenceDownloadFlow.largeDownloadExcelWarning" />
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Summary Sidebar */}
        <div className="lg:g-col-span-1">
          <div className="g-bg-white g-rounded g-shadow-md g-border g-border-gray-200 g-p-6 g-sticky g-top-6">
            <h3 className="g-font-semibold g-text-gray-900 g-mb-4">
              <FormattedMessage id="occurrenceDownloadFlow.downloadSummary" />
            </h3>

            <DownloadSummary
              selectedFormat={selectedFormat}
              configuration={configuration}
              sequencedRecords={sequencedRecords}
            />

            <div className="g-space-y-3 g-mb-6 g-mt-6">
              <h4 className="g-font-medium g-text-gray-900">
                <FormattedMessage id="occurrenceDownloadFlow.termsStatus" />
              </h4>
              <Term
                label={<FormattedMessage id="occurrenceDownloadFlow.dataUseAgreementTitle" />}
                termKey="dataUse"
                accepted={acceptedTerms.dataUse}
                handleTermChange={handleTermChange}
              />
              <Term
                label={<FormattedMessage id="occurrenceDownloadFlow.dataCitationTitle" />}
                termKey="dataCitation"
                accepted={acceptedTerms.dataCitation}
                handleTermChange={handleTermChange}
              />
              {isLargeDownload && (
                <Term
                  label={
                    <FormattedMessage id="occurrenceDownloadFlow.largeDownloadAcknowledgment" />
                  }
                  termKey="largeDownload"
                  accepted={acceptedTerms.largeDownload || false}
                  handleTermChange={handleTermChange}
                />
              )}
            </div>

            {blockedByMissingSequences && (
              <div className="g-text-red-600 g-text-sm g-font-medium g-mb-4">
                <FormattedMessage
                  id="occurrenceDownloadFlow.sequences.noSequencesDescription"
                  defaultMessage="This format is only available for searches that contain DNA sequences. Add a filter that selects sequenced records to use it."
                />
              </div>
            )}

            {error && <div className="g-text-red-600 g-text-sm g-font-medium g-mb-4">{error}</div>}

            <Button
              disabled={!allTermsAccepted || preparingDownload || blockedByMissingSequences}
              className={`g-w-full g-flex g-items-center g-justify-center g-gap-2`}
              onClick={handleDownload}
            >
              <FaDownload size={16} />
              <FormattedMessage id="occurrenceDownloadFlow.createDownload" />
            </Button>

            {!allTermsAccepted && (
              <p className="g-text-xs g-text-gray-500 g-text-center g-mt-2">
                <FormattedMessage id="occurrenceDownloadFlow.completeRequirements" />
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Term({
  termKey,
  accepted,
  handleTermChange,
  label,
}: {
  termKey: AcceptedTerm;
  accepted: boolean;
  handleTermChange: (term: AcceptedTerm) => void;
  label: string | React.ReactNode;
}) {
  return (
    <label className="g-flex g-items-start g-gap-2 g-text-sm">
      <Checkbox
        id={termKey}
        checked={accepted}
        onCheckedChange={() => handleTermChange(termKey)}
        className="'g-flex-none g-me-2 g-mt-0.5 g-h-4 g-w-4"
      />
      <span className={accepted ? 'g-text-green-700' : 'g-text-gray-600'}>{label}</span>
    </label>
  );
}
